import { useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  CalendarDays,
  Images,
  History,
  LayoutDashboard,
  LogOut,
  MonitorSmartphone,
  Settings,
  Shield,
  Timer,
  Users,
  Wallet,
} from 'lucide-react'
import { toast } from 'sonner'
import { useProductAuth } from '@orisini/ui/context/AuthContext'
import { api } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'

/**
 * `match` dipakai untuk item yang jalurnya bertumpuk: tanpa itu "/posts" ikut
 * menyala saat berada di "/posts/bulk" sehingga dua item tampak aktif sekaligus.
 */
const NAV: Array<{
  to: string
  label: string
  icon: typeof LayoutDashboard
  end?: boolean
  match?: (pathname: string) => boolean
}> = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays },
  { to: '/posts', label: 'Posts', icon: Images },
  { to: '/slots', label: 'Slots', icon: Timer },
  { to: '/history', label: 'History', icon: History },
  { to: '/accounts', label: 'Instagram accounts', icon: Users },
  { to: '/agent', label: 'Devices', icon: MonitorSmartphone },
  { to: '/billing', label: 'Subscription', icon: Wallet },
  { to: '/settings', label: 'Settings', icon: Settings },
]

export default function AppShell() {
  const { me, logout } = useProductAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent'>('idle')

  async function onLogout() {
    await logout()
    toast.success('You have signed out')
    navigate('/login', { replace: true })
  }

  /**
   * `/api/auth/*` diteruskan apa adanya ke orisini, jadi ini tetap dilayani
   * Worker identitas meski dipanggil dari origin Gramium. Endpointnya selalu
   * membalas sukses (anti enumerasi email), maka statusnya tidak dibedakan.
   */
  async function onResend() {
    if (!me) return
    setResend('sending')
    try {
      await api.post('/auth/resend-verification', { email: me.user.email })
      setResend('sent')
    } catch {
      setResend('idle')
      toast.error("Couldn't resend the verification email")
    }
  }

  return (
    <div className="flex min-h-dvh">
      <aside className="flex w-60 shrink-0 flex-col border-r bg-card">
        <div className="px-5 py-5">
          <span className="text-lg font-semibold tracking-tight">Gramium</span>
        </div>

        <nav className="flex-1 space-y-0.5 px-3">
          {NAV.map(({ to, label, icon: Icon, end, match }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  (match ? match(pathname) : isActive)
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )
              }
            >
              <Icon className="size-4" />
              {label}
            </NavLink>
          ))}

          {me?.user.role === 'admin' && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'mt-2 flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )
              }
            >
              <Shield className="size-4" />
              Admin
            </NavLink>
          )}
        </nav>

        <div className="border-t p-3">
          <div className="px-2 pb-2">
            <p className="truncate text-sm font-medium">{me?.user.name || me?.user.email}</p>
            <p className="text-xs text-muted-foreground">
              {me?.plan.name} plan · {me?.usage.remaining} posts left on all platforms
            </p>
          </div>
          <button
            onClick={onLogout}
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
          >
            <LogOut className="size-4" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {!me?.user.emailVerified && (
          <div className="border-b border-amber-500/30 bg-amber-500/10 px-6 py-2 text-sm text-amber-200">
            Your email isn't verified yet. Check your inbox for the verification link.{' '}
            {resend === 'sent' ? (
              <span className="font-medium">Verification email resent.</span>
            ) : (
              <>
                Didn't get the email?{' '}
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resend === 'sending'}
                  className="font-medium underline underline-offset-2 disabled:opacity-60"
                >
                  {resend === 'sending' ? 'Sending…' : 'Resend'}
                </button>
              </>
            )}
          </div>
        )}
        {!me?.plan.active && (
          <div className="border-b border-destructive/40 bg-destructive/10 px-6 py-2 text-sm text-destructive-foreground">
            Subscription inactive — scheduling is paused. Your queue is kept.{' '}
            <Link to="/billing" className="font-medium underline underline-offset-2">
              Enable now
            </Link>
          </div>
        )}
        <main className="min-w-0 flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
